import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Markdown from 'markdown-to-jsx';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import { CopilotToggleCustomEvent, type ILogger } from '@bingads-webui-react/msa-copilot-common';

import { useSummarizeMessage } from '../../hooks/use-summarize-message';
import { InsightsSummarizePluginResult } from './insights-summarize-plugin-result';
import { useInsightsSummarizeMessageStyles } from './use-insights-summarize-message-styles';
import { sanitizeDateFromSummarizePlugin } from './sanitize-date-from-summarize-plugin';

export type InsightsSummarizeMessageProps = {
  logger?: ILogger;
  replyToId: string;
  isCustomizedEventBusy?: boolean;
  isReportingInsights15Enabled?: boolean;
};

const allCampaignsFluctuationTriggerMessage = _TL_('Analyze the trends and fluctuations in my account from {{startDate}} to {{endDate}}');
const singleCampaignFluctuationTriggerMessage = _TL_('Analyze the trends and fluctuations in "{{campaignName}}" campaign from {{startDate}} to {{endDate}}');

const MAX_START_DATE_OFFSET = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds

export const InsightsSummarizeMessage: React.FC<InsightsSummarizeMessageProps> = React.memo((props) => {
  const {
    logger,
    replyToId,
    isCustomizedEventBusy = false,
    isReportingInsights15Enabled,
  } = props;

  const [isFluctuationsMessageSent, setIsFluctuationsMessageSent] = useState(false);
  const i18n = useI18nContext();
  const classes = useInsightsSummarizeMessageStyles();

  const {
    hasNoData,
    shouldSendFluctuationsTriggerMessage,
    summarizeMessage,
    summarizePluginResult,
  } = useSummarizeMessage(replyToId);

  useEffect(() => {
    if (logger && summarizePluginResult) {
      // log only after plugin result has been received
      logger?.trace(`InsightsSummarizeMessage: replyToId: ${replyToId}, hasNoData: ${hasNoData}, shouldSendFluctuationsTriggerMessage: ${shouldSendFluctuationsTriggerMessage}`);
    }
  }, [
    hasNoData,
    logger,
    replyToId,
    shouldSendFluctuationsTriggerMessage,
    summarizePluginResult,
  ]);

  const parseDate = useCallback<(dateString: string) => Date>((dateString) => {
    const convertISOToDate = i18n.dateParser({ raw: 'yyyy-MM-dd' });
    const sanitizedDateString = sanitizeDateFromSummarizePlugin(dateString);
    return convertISOToDate(sanitizedDateString);
  }, [i18n]);

  useEffect(() => {
    if (!isCustomizedEventBusy && !isFluctuationsMessageSent && shouldSendFluctuationsTriggerMessage) {
      const startDateString = summarizePluginResult?.Parameters?.StartDate;
      const endDateString = summarizePluginResult?.Parameters?.EndDate;

      try {
        // parse the startDate and endDate from summarizePluginResult, startDate should not be more than 90 days in the past
        const minStartDate = new Date(Date.now() - MAX_START_DATE_OFFSET);
        const startDateFromParameters = parseDate(startDateString);

        const startDate = startDateFromParameters > minStartDate ? startDateFromParameters : minStartDate;
        const endDate = parseDate(endDateString);

        // create the message to trigger the fluctuations plugin
        const campaignName = summarizePluginResult?.Parameters?.CampaignName || '';
        const message = i18n?.getString(
          campaignName ? singleCampaignFluctuationTriggerMessage : allCampaignsFluctuationTriggerMessage,
          {
            campaignName,
            startDate: i18n.formatDate(startDate),
            endDate: i18n.formatDate(endDate),
          },
        );

        if (endDate >= startDate) {
          // send a user message to trigger fluctuations plugin
          const customEvent = new CopilotToggleCustomEvent({
            detail: {
              eventType: 'message',
              eventSource: 'insightsSummarizeMessage',
              eventData: {
                isHidden: true,
                message,
                pluginName: campaignName ? 'CampaignFluctuation' : 'AccountFluctuation',
                startDate: i18n.formatDate(startDate, { raw: 'yyyy-MM-dd' }),
                endDate: i18n.formatDate(endDate, { raw: 'yyyy-MM-dd' }),
              },
            },
          });

          logger?.trace(`InsightsSummarizeMessage: Sending fluctuations trigger message for ${replyToId}: ${message}`);
          document.dispatchEvent(customEvent);
          setIsFluctuationsMessageSent(true);
        } else {
          logger?.trace(`InsightsSummarizeMessage: endDate (${endDate}) is before startDate (${startDate}). No fluctuation message sent.`);
        }
      } catch (e) {
        logger?.trace(`InsightsSummarizeMessage: Error parsing dates, startDateString=${startDateString}, endDateString=${endDateString}, error=${e}`);
      }
    }
  }, [
    i18n,
    logger,
    parseDate,
    replyToId,
    shouldSendFluctuationsTriggerMessage,
    summarizePluginResult,
    isCustomizedEventBusy,
  ]);

  const summarizeText = useMemo<string | undefined>(() => {
    if (!summarizeMessage) {
      return undefined;
    }
    if (hasNoData === false) {
      return `**${i18n?.getString(_TL_('Analysis'))}**\n\n${summarizeMessage.text}`;
    }
    return summarizeMessage.text;
  }, [hasNoData, i18n, summarizeMessage?.text]);

  if (!summarizeMessage) {
    return null;
  }

  return (
    <>
      {!hasNoData && (
        <InsightsSummarizePluginResult
          className={classes.pluginResult}
          summarizePluginResult={summarizePluginResult}
          isReportingInsights15Enabled={isReportingInsights15Enabled}
        />
      )}
      <Markdown className="content">
        {summarizeText}
      </Markdown>
    </>
  );
});
